<template>
    <PageLoading v-if="isLoading" />
    <div v-else class="main-wrap course-timetable-page">
        <h1>{{ $t('general.timetable') }}</h1>
        <div class="row justify-between col-12">
            <!-- course title -->
            <div class="col">
                <h4 v-if="hasCourse">
                    {{
                        JSONParseTranslations(
                            libraryCourse.coursesOnLibrary.title,
                            locale.key
                        )
                    }}
                </h4>
            </div>
            <!-- buttons -->
            <div class="row col-1 justify-end">
                <q-btn
                    class="view-btn"
                    icon="list"
                    :text-color="!calenderView ? 'white' : 'black'"
                    :color="!calenderView ? 'secondary' : 'white'"
                    @click="calenderView = false"
                >
                </q-btn>
                <q-btn
                    class="view-btn"
                    icon="event_note"
                    :text-color="calenderView ? 'white' : 'black'"
                    :color="calenderView ? 'secondary' : 'white'"
                    @click="calenderView = true"
                >
                </q-btn>
            </div>
        </div>
        <Table
            v-show="!calenderView"
            :columns="columns"
            :data="lessons"
            :pagination="pagination"
            :filter="filter"
            :loading="isFetchingLessons"
            @request="onRequest"
        >
        </Table>
        <Calendar v-show="calenderView" :lessons="lessons" />
    </div>
</template>

<script>
import PageLoading from '@/components/PageLoading'
import lessonsService from '@/services/modules/lessons'
import libraryService from '@/services/modules/libraries'
import enrollmentsServices from '@/services/modules/enrollments'
import store from '@/store'
import Table from '@/components/lessons/Table'
import Calendar from '@/components/lessons/Calendar'
import translate from '@/local-shared/mixins/translate'

const loadingKeys = {
    fetchingLibrary: 'fetching-library',
    fetchingLessons: 'fetching-lessons',
    fetchEnrollments: 'fetching-enrollments',
}

export default {
    name: 'CourseTimetable',

    components: {
        PageLoading,
        Table,
        Calendar,
    },

    mixins: [translate],

    data() {
        return {
            library: [],
            hasCourse: false,
            libraryCourse: {},
            lessons: [],
            enrollment: [],
            calenderView: false,
            pagination: {
                sortBy: 'created_at',
                descending: true,
                page: 1,
                rowsPerPage: 20,
                rowsNumber: 0,
            },
            filter: {},
        }
    },

    computed: {
        columns() {
            return [
                {
                    name: 'trainers',
                    label: this.$tc('general.trainers', 2),
                    field: (row) => row.lessonTrainers,
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'classroom',
                    label: this.$tc('general.classrooms', 2),
                    field: (row) => row.classrooms.name,
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'start_date',
                    label: this.$tc('lessons.start_date'),
                    field: 'start_date',
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'end_date',
                    label: this.$t('lessons.end_date'),
                    field: 'end_date',
                    align: 'left',
                    sortable: true,
                },
            ]
        },

        isLoading() {
            return (
                this.isFetchingLibrary ||
                this.isFetchingLessons ||
                this.isFetchingEnrollments
            )
        },

        isFetchingLibrary() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingLibrary
            ).length
        },

        isFetchingLessons() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingLessons
            ).length
        },

        isFetchingEnrollments() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchEnrollments
            ).length
        },

        userLogged() {
            return store.getters['session/user']
        },

        locale() {
            return store.getters['locale/currentLocale']
        },
    },

    async created() {
        this.courseId = this.$route.params.id

        // check if user is owner of this course (check library)
        // if not, redirects to course details and triggers notification with text to buy course
        // else continue to load course status and setup scorm window api object and course launch

        if (this.userLogged) {
            await this.fetchLibrary()
            await this.ifOwnsCourse()
            if (!this.hasCourse) {
                this.$router.push({
                    name: 'CourseDetails',
                    params: { id: this.courseId },
                })
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('notify.cannot_access_course'),
                })
            } else {
                await this.fetchEnrollments()
                if (this.enrollment.length) {
                    this.fetchLessons()
                }
            }
        } else {
            this.$router.push({
                name: 'CourseDetails',
                params: { id: this.courseId },
            })
            this.$q.notify({
                type: 'negative',
                message: this.$t('errors.user_must_be_logged_in'),
            })
        }
    },

    methods: {
        async fetchLibrary() {
            this.loadingAdd(loadingKeys.fetchingLibrary)

            let parameters = {
                filter: {
                    user: this.userLogged.id,
                },
            }

            let response = await libraryService.retrieve(parameters)

            if (response.data.status === 'success') {
                this.library = response.data.results
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('errors.library_error'),
                })
            }

            this.loadingRemove(loadingKeys.fetchingLibrary)
        },

        async ifOwnsCourse() {
            if (this.library.length && this.courseId) {
                let course = this.library.find(
                    (item) => item.course_id === parseInt(this.courseId)
                )
                this.hasCourse = course ? true : false
                this.libraryCourse = course
            }
        },

        async fetchEnrollments() {
            this.loadingAdd(loadingKeys.fetchEnrollments)

            let parameters = {
                filter: {
                    id: this.$route.params.enrollmentId,
                },
            }

            let response = await enrollmentsServices.retrieve(parameters)

            if (response.status === 200) {
                this.enrollment = response.data.results
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchEnrollments)
        },

        mapColumn(value) {
            if (value == null) {
                return this.pagination.sortBy
            }
            if (value == 'classroom') {
                return 'classrooms.name'
            }

            return value
        },

        mapOrder(value) {
            return value == true ? 'desc' : 'asc'
        },

        async fetchLessons() {
            this.loadingAdd(loadingKeys.fetchingLessons)

            let parameters = {
                filter: {
                    class_id: this.enrollment[0].class_id,
                    enabled: true,
                },
                pagination: {
                    perPage: this.pagination.rowsPerPage,
                    currentPage: this.pagination.page,
                },
                orderBy: [
                    {
                        column: this.mapColumn(this.pagination.sortBy),
                        direction: this.mapOrder(this.pagination.descending),
                    },
                ],
                relations: {
                    classes: true,
                    classrooms: true,
                    lessonTrainers: true,
                    course: true,
                },
            }

            let response = await lessonsService.retrieve(parameters)

            if (response.status === 200) {
                this.lessons = response.data.results
                this.pagination.rowsNumber = response.data.pagination.total
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingLessons)
        },

        async onRequest(paginationAux, filterAux) {
            this.filter = filterAux
            this.pagination = paginationAux
            await this.fetchLessons()
            // if (this.calendarView == true) {
            //     await this.mapLessonsForCalendarView()
            // }
        },
    },
}
</script>
